import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaPaperPlane, FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import "./Chat.scss";

type Role = "user" | "assistant" | "system";

interface Message {
    id: string;
    role: Role;
    content: string;
    created_at?: string;
}

const makeId = () =>
    Math.random().toString(36).slice(2, 9) + "-" + Date.now().toString(36);

const Chat: React.FC = () => {
    const params = useParams<{ id?: string }>();
    const searchParams = new URLSearchParams(window.location.search);
    const navigate = useNavigate();
    const conversationId = params?.id ?? searchParams.get("id") ?? makeId();

    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [showDetail, setShowDetail] = useState<Message | null>(null);
    const [error, setError] = useState<string | null>(null);

    // refs
    const listRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);

    const axiosInstance = axios.create({
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
        timeout: 120000,
    });

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    // Attempt refresh
                    await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
                    // Retry original request
                    return axiosInstance(originalRequest);
                } catch {
                    // Refresh failed → redirect to login
                    navigate("/login");
                    return Promise.reject(error);
                }
            }
            return Promise.reject(error);
        }
    );

    useEffect(() => {
        let cancelled = false;
        async function loadConversation() {
            setError(null);
            try {
                const res = await axiosInstance.get(`${process.env.REACT_APP_API_BASE_URL}/conversations/${conversationId}`);
                if (!cancelled) {
                    setMessages(res.data?.messages ?? []);
                    // attempt scroll after initial load
                    requestAnimationFrame(() => scrollToBottom());
                }
            } catch (err: any) {
                const fallback = localStorage.getItem(`conv:${conversationId}`);
                if (fallback && !cancelled) {
                    setMessages(JSON.parse(fallback));
                } else if (!cancelled) {
                    setError("Failed to load conversation.");
                }
            }
        }
        //loadConversation();
        return () => {
            cancelled = true;
        };
    }, [conversationId]);

    useEffect(() => {
        try {
            localStorage.setItem(`conv:${conversationId}`, JSON.stringify(messages));
        } catch {
            // ignore
        }
    }, [messages, conversationId]);

    // Reliable scroll: use sentinel + requestAnimationFrame
    const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
        if (endRef.current) {
            try {
                endRef.current.scrollIntoView({ behavior, block: "nearest" });
            } catch {
                // fallback to element scroll
                if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                }
            }
        } else if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    };

    // Auto-scroll when messages change. Use requestAnimationFrame to ensure render completed.
    useEffect(() => {
        // if you prefer smooth only when user is not scrolled up, you can check scroll position here
        requestAnimationFrame(() => {
            // a small timeout helps when large items/images load in
            setTimeout(() => scrollToBottom("smooth"), 0);
        });
    }, [messages]);

    const handleSend = async () => {
        const trimmed = input.trim();
        if (!trimmed) return;
        setError(null);

        const userMsg: Message = {
            id: makeId(),
            role: "user",
            content: trimmed,
            created_at: new Date().toISOString(),
        };
        const assistantPlaceholder: Message = {
            id: makeId(),
            role: "assistant",
            content: "",
            created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
        setInput("");
        inputRef.current?.focus();
        setLoading(true);

        try {
            const res = await axiosInstance.post(`${process.env.REACT_APP_API_BASE_URL}/chat`, { message: trimmed });

            let replyText = "";
            if (res.data?.response) replyText = res.data.response;
            else if (res.data?.reply) replyText = res.data.reply;
            else replyText = JSON.stringify(res.data);

            setMessages((prev) => {
                const idx = prev.map((m) => m.role).lastIndexOf("assistant");
                if (idx === -1) return [...prev, { ...assistantPlaceholder, content: replyText }];
                const copy = [...prev];
                copy[idx] = { ...copy[idx], content: replyText, created_at: new Date().toISOString() };
                return copy;
            });
        } catch (err: any) {
            setMessages((prev) => prev.filter((m) => !(m.role === "assistant" && m.content === "")));
            setError(err?.message ?? "Failed to send message.");
            setMessages((prev) => [
                ...prev,
                { id: makeId(), role: "assistant", content: `Error: ${err?.message ?? "server error"}`, created_at: new Date().toISOString() },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const openDetail = (m: Message) => {
        setShowDetail(m);
    };

    const closeDetail = () => setShowDetail(null);

    return (
        <div className="chat-container">
            <div className="chat-header">
                <h1>Conversation</h1>
                <div className="conv-info">
                    <span className="conv-id">ID: {conversationId}</span>
                </div>
            </div>

            <div className="chat-main">
                {/* SCROLLABLE MESSAGES */}
                <div className="chat-body" ref={listRef}>
                    {messages.length === 0 && !error && (
                        <div className="empty-state">
                            <p>No messages yet — say hello 👋</p>
                        </div>
                    )}

                    {messages.map((m) => (
                        <div
                            key={m.id}
                            className={`message-row ${m.role === "user" ? "user" : "assistant"}`}
                            onClick={() => openDetail(m)}
                            role="button"
                            tabIndex={0}
                        >
                            {m.role === "assistant" && <div className="avatar assistant-avatar"><FaUserCircle /></div>}
                            <div className="message-bubble">
                                <div className="message-content" dangerouslySetInnerHTML={{ __html: escapeHtml(m.content) }} />
                                <div className="message-meta">
                                    <small>{m.role}</small>
                                    {m.created_at && <small className="ts">{new Date(m.created_at).toLocaleString()}</small>}
                                </div>
                            </div>
                            {m.role === "user" && <div className="avatar user-avatar"><FaUserCircle /></div>}
                        </div>
                    ))}

                    {/* sentinel at the end for reliable scrollIntoView */}
                    <div ref={endRef} style={{ height: 4 }} />
                </div>

                {/* INPUT BAR OUTSIDE SCROLLABLE AREA */}
                <div className="chat-input-bar">
                    <textarea
                        ref={inputRef}
                        className="chat-input"
                        placeholder={loading ? "Waiting for response..." : "Type a message and press Enter"}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        disabled={loading}
                    />
                    <button className="send-btn" onClick={handleSend} disabled={loading || !input.trim()}>
                        <FaPaperPlane />
                    </button>
                </div>
            </div>

            {error && <div className="chat-error">{error}</div>}

            {showDetail && (
                <div className="popup-overlay" onClick={closeDetail}>
                    <div className="popup-view" onClick={(e) => e.stopPropagation()}>
                        <h2>Message Detail</h2>
                        <div className="detail-row"><strong>Role:</strong> <span>{showDetail.role}</span></div>
                        <div className="detail-row"><strong>Sent:</strong> <span>{showDetail.created_at ?? "Unknown"}</span></div>
                        <div className="detail-row"><strong>Content:</strong>
                            <div className="message-full">{showDetail.content || "—"}</div>
                        </div>
                        <div className="details-actions">
                            <button onClick={closeDetail}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function escapeHtml(text: string) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, "<br/>");
}

export default Chat;
